import {
  contentEditableClass,
  contentEditableClassWithIcon,
  contentEditableMultilinePlaceholderClass,
  contentEditableNoPaddingClass,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
  type IconName,
} from "@prismicio/editor-ui"
import type { RichText } from "@prismicio/types-internal/lib/customtypes"
import {
  type Content,
  Extension,
  type Extensions,
  getSchema,
  type Node,
} from "@tiptap/core"
import { History } from "@tiptap/extension-history"
import ListKeymap from "@tiptap/extension-list-keymap"
import type { Schema } from "@tiptap/pm/model"
import { EditorContent, useEditor } from "@tiptap/react"
import clsx from "clsx"
import { useEffect, useMemo, useRef } from "react"

import { useEditorConfig } from "../../EditorConfig"
import { getFieldLabel } from "../utils"
import { BubbleMenu } from "./BubbleMenu"
import { CoreComponents, Document } from "./coreExtensions"
import { TableFloatingMenus } from "./extensions/Table/menus"
import { TableLimitToast } from "./extensions/Table/TableLimitToast"
import { TextDirection } from "./globalExtensions"
import {
  ExtensionType,
  type NodeExtension,
  NodeType,
  type WidgetExtension,
} from "./models"
import { composeExtensions } from "./models/RichTextConfig"
import type { ExtendedRichTextContent, ExtendedRichTextNodeType } from "./types"

interface Props<TContent extends ExtendedRichTextContent> {
  id: string
  content: TContent | undefined
  field: RichText
  readOnly: boolean
  padding?: "normal" | "zero"
  scroll?: "hidden" | "x-auto"
  onContentChange: (content: TContent | undefined) => void
  /**
   * Checks `editor.isEmpty` before calling `onContentChange`.
   * - When `true`, the current structure of the document will be returned, even if it has no content.
   * - When `false`, `undefined` will be returned.
   * @default false
   */
  shouldReturnEmptyContent?: boolean
  icon?: IconName
}

export function RichTextField<TContent extends ExtendedRichTextContent>(
  props: Props<TContent>,
) {
  const {
    content,
    field,
    id,
    readOnly,
    padding = "normal",
    scroll = "hidden",
    shouldReturnEmptyContent = false,
    icon,
    onContentChange,
  } = props

  const { useThreads, CustomThreads } = useEditorConfig()

  // The slice id uses Reacts useId hook to generate a unique id for the slice
  // however, this id is not stable and changes the initial part on every render
  // so we need to extract the stable part of the id to use here
  // example ID: :r1g7:.slices[integration_fields_slice$851d97a2-7d97-4564-8ead-12307830ed9d][181].productTitle
  const fieldId = id.split(":")[2] || id // .slices[integration_fields_slice$851d97a2-7d97-4564-8ead-12307830ed9d][181].productTitle

  const conditionalExtensions = useConditionalExtensions({
    fieldId,
  })

  const threads = useThreads?.().threads

  const config = useConfig({
    field,
    conditionalExtensions,
  })

  const lastLocalContentUpdate = useRef<{ content: TContent | undefined }>()
  const lastUpdateWasLocal = useRef(false)

  const proseMirrorContent = useMemo(() => {
    if (!content) return undefined

    return Document.converter(config.nodeExtensions)
      .fromPrismic(fieldId, config.schema, config.nodeTypes, content, threads)
      ?.toJSON() as Content | undefined
  }, [config, content, fieldId, threads])

  const getContentEditableClass = () => {
    if (padding === "zero") return contentEditableNoPaddingClass
    if (icon) return contentEditableClassWithIcon
    return contentEditableClass
  }

  const editor = useEditor(
    {
      content: proseMirrorContent,
      editable: !readOnly,
      editorProps: {
        attributes: {
          class: clsx(
            getContentEditableClass(),
            contentEditableMultilinePlaceholderClass,
          ),
          id,
          tabIndex: "0",
        },
      },
      extensions: config.allComponents,
      injectCSS: false,
      onUpdate({ editor }) {
        let newContent: TContent | undefined

        if (editor.isEmpty && !shouldReturnEmptyContent) {
          newContent = undefined
        } else {
          newContent = Document.converter<TContent>(
            config.nodeExtensions,
          ).toPrismic(editor.state.doc)
        }

        lastLocalContentUpdate.current = { content: newContent }
        lastUpdateWasLocal.current = true

        onContentChange(newContent)
      },
    },
    [config, readOnly],
  )

  useEffect(() => {
    if (!editor?.isInitialized) return

    const isLocalUpdate = lastUpdateWasLocal.current
    lastUpdateWasLocal.current = false

    // This useEffect is meant to apply change from external source,
    // if it's a local update, we don't need to do anything
    if (isLocalUpdate) return

    // Tiptap does not play well if we treat it like a controlled component:
    // https://github.com/ueberdosis/tiptap/issues/4592
    // If we have a local update and it's the same as the content coming from
    // the external source, means we are in sync already and we don't need to do anything
    if (
      lastLocalContentUpdate.current &&
      lastLocalContentUpdate.current.content === content
    )
      return

    editor.commands.setContent(proseMirrorContent ?? null, false)
  }, [editor, content, proseMirrorContent])

  // editor will be initialized on the first render, we're just narrowing the type here.
  // https://github.com/ueberdosis/tiptap/blob/v2.11.5/packages/react/src/useEditor.ts#L119
  if (!editor) return null

  /**
   * Note that with Tiptap 2.3.0, BubbleMenu MUST be declared before EditorContent 😅
   * https://github.com/ueberdosis/tiptap/issues/4619#issuecomment-1869042861
   */
  return (
    <Field display={scroll === "x-auto" ? "grid" : "flex"}>
      {icon && <FieldIcon name={icon} />}
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        {editor.isEditable && (
          <BubbleMenu
            editor={editor}
            nodeTypes={config.names}
            labels={config.labels}
            hideDisabledOptions
          />
        )}
        {editor.isEditable && config.names.includes("table") && (
          <>
            <TableFloatingMenus editor={editor} />
            <TableLimitToast editor={editor} />
          </>
        )}
        <EditorContent editor={editor} style={{ width: "100%" }} />
        {CustomThreads && <CustomThreads editor={editor} threads={threads} />}
      </FieldControl>
    </Field>
  )
}

function useConfig({
  field,
  conditionalExtensions,
}: {
  field: RichText
  conditionalExtensions: (Extension | Node)[]
}): Config {
  // Tiptap's `useEditor` doesn't allow to dynamically change the list of extensions.
  // To fix this, trigger a re-mount with this key by re-computing the useMemo.
  const extensionsKey = `${field.config?.multi ?? ""}-${
    field.config?.single ?? ""
  }`

  return useMemo(() => {
    const { extensions, nodeTypes } = composeExtensions(field.config)

    const nodeExtensions = extensions.filter(extensionIsTypeNode)

    const names = extensions.flatMap((e) => e.richTextNodeTypes)
    if (nodeTypes.has(TextDirection.richTextNodeType)) {
      names.push(TextDirection.richTextNodeType)
    }

    const components = extensions.flatMap((e) => e.component)

    const supportedExtensionNames = extensions
      .filter(extensionIsSupported)
      .map((extension) => extension.name)

    const allComponents = [
      ...conditionalExtensions,
      ...components,
      ...CoreComponents(field, nodeExtensions),
      TextDirection.component.configure({ supportedExtensionNames }),
      ListKeymap,
    ]

    const extensionNames = allComponents.map((c) => c.name)

    const schema = getSchema(allComponents)
    const labels = getLabels(field.config?.labels)

    return {
      allComponents,
      extensionNames,
      nodeTypes,
      nodeExtensions,
      names,
      schema,
      labels,
    }
    // We don't want to recompute this config on every render and field is unstable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionsKey])
}

function extensionIsTypeNode(value: WidgetExtension): value is NodeExtension {
  return value.extType === ExtensionType.Node
}

function extensionIsSupported(extension: WidgetExtension) {
  if (extension.extType !== ExtensionType.Node) return false
  if (extension.nodeType !== NodeType.Text) return false
  if (extension.lockedFormat) return false
  return true
}

function getLabels(labels: readonly string[] | undefined) {
  // Convert prismic model readonly array to a standard array of labels
  return Array.from(labels ?? [])
}

interface Config {
  allComponents: Extensions
  extensionNames: string[]
  nodeTypes: Set<ExtendedRichTextNodeType>
  nodeExtensions: NodeExtension[]
  names: ExtendedRichTextNodeType[]
  schema: Schema
  labels: string[]
}

interface UseConditionalExtensionsArgs {
  fieldId: string
}

/**
 * Returns the conditional extensions for a rich text field.
 * If the field is using Liveblocks, it will return the Liveblocks extension.
 * Otherwise, it will return the History extension.
 *
 * This is necessary because the Liveblocks extension has its own History extension.
 *
 * If the room is available we don't need the History extension or the history from the Liveblocks extension.
 * As we are using the room history, a separate history stack tied to the Liveblocks room and not TipTap.
 */
function useConditionalExtensions(args: UseConditionalExtensionsArgs) {
  const { fieldId } = args
  const { useCollaborativeComments, hasSharedCollaborativeHistory } =
    useEditorConfig()

  const liveblocksExtension = useCollaborativeComments?.({
    field: fieldId,
  })

  if (liveblocksExtension) {
    return [
      liveblocksExtension.configure({
        history: hasSharedCollaborativeHistory ? false : true,
      }),
    ]
  }

  return hasSharedCollaborativeHistory ? [] : [History]
}
